import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import { useEffect, useRef } from 'react';
import isEqual from 'react-fast-compare';
import type { useRegistryContext } from '../../../context/RegistryProvider';
import type { createGlobalStateStoreSelectors } from '../selectors';
import type { BaseGlobalStateStoreProviderProps } from '../types';
import { mergeBlockOverride } from '../utils/mergeBlockOverride';

type Store = ReturnType<typeof createGlobalStateStoreSelectors>;
type Registry = ReturnType<typeof useRegistryContext>['registry'];

type BlocksMap = Record<string, BlockType | undefined> | undefined;

export function useSyncComponentOverrides({
  store,
  blocks,
  componentOverrides,
  registry,
  getDependencyGraphActions,
}: {
  // null when the caller is a per-instance wrapper that has not yet resolved
  // the instance store. Effect short-circuits — see useSyncInstanceGlobalState.
  store: Store | null;
  blocks: BlocksMap;
  componentOverrides: BaseGlobalStateStoreProviderProps['componentOverrides'];
  registry: Registry;
  getDependencyGraphActions: BaseGlobalStateStoreProviderProps['getDependencyGraphActions'];
}) {
  // Component Overrides v2 reactive sync: when the parent TC's overrides map changes
  // for a given child blockId (added / changed / removed), recompute that
  // block's initial state from the merged authored+override block and push
  // it via setBlocksState. We diff per-blockId against a ref-tracked previous
  // overrides snapshot so unrelated children skip a state write.
  const prevComponentOverridesRef = useRef(componentOverrides);
  useEffect(() => {
    if (!store) return;
    const prev = prevComponentOverridesRef.current;
    if (isEqual(prev, componentOverrides)) return;
    if (!blocks) {
      prevComponentOverridesRef.current = componentOverrides;
      return;
    }

    const prevKeys = prev ? Object.keys(prev) : [];
    const nextKeys = componentOverrides ? Object.keys(componentOverrides) : [];
    const affected = new Set<string>([...prevKeys, ...nextKeys]);
    const nextBlocksState: Record<string, BlockStateUnionType> = {};
    const changedBlockIds: string[] = [];

    affected.forEach((blockId) => {
      const authored = blocks[blockId];
      if (!authored) return;
      const prevOverride = prev?.[blockId];
      const nextOverride = componentOverrides?.[blockId];
      if (isEqual(prevOverride, nextOverride)) return;
      const merged = mergeBlockOverride(authored, nextOverride);
      const initialState = registry.getBlockInitialState(merged.component.componentType, merged);
      if (!initialState) {
        console.error(`No initial state found for block ${blockId}`);
        return;
      }
      nextBlocksState[blockId] = initialState as BlockStateUnionType;
      changedBlockIds.push(blockId);
    });

    if (changedBlockIds.length > 0) {
      store.getState().actions.setBlocksState(nextBlocksState);
      const notify = getDependencyGraphActions().notifyDependencyChange;
      changedBlockIds.forEach((blockId) => {
        notify({ id: blockId });
      });
    }

    prevComponentOverridesRef.current = componentOverrides;
  }, [blocks, componentOverrides, getDependencyGraphActions, registry, store]);
}
